import { MutableRefObject, useCallback, useEffect, useRef, useState } from 'react'
import { fetchAccessToken, refreshAccessToken } from '../../React/Common/API/AccessTokenClient'
import { ReactState } from '../../React/Common/Types/Types'
import { TokenResponse } from '../../NonReact/Types/Types'
import { IsVarEmpty } from '../../NonReact/utils/GlobalFunctions'

export type AccessToken = {
    accessToken: Nullable<string>
    renewToken: () => Promise<void>
}

/**
 * Custom React hook to manage access token retrieval and renewal.
 */
export const useAccessToken = (): AccessToken => {
    const [accessToken, setAccessToken]: ReactState<Nullable<string>> = useState<Nullable<string>>(null)
    const refreshTokenRef: MutableRefObject<Nullable<string>> = useRef<Nullable<string>>(null)

    const getInitialToken = useCallback(async (): Promise<void> => {
        const response: Nullable<TokenResponse> = await fetchAccessToken()
        if (IsVarEmpty(response)) return

        setAccessToken(response.AccessToken)
        refreshTokenRef.current = response.RefreshToken
    }, [])

    const renewToken = useCallback(async (): Promise<void> => {
        const storedRefreshToken: Nullable<string> = refreshTokenRef.current

        if (!storedRefreshToken) {
            await getInitialToken()
            return
        }

        const response: Nullable<TokenResponse> = await refreshAccessToken(storedRefreshToken)

        if (response) {
            setAccessToken(response.AccessToken)
            refreshTokenRef.current = response.RefreshToken
        } else {
            refreshTokenRef.current = null
            await getInitialToken()
        }
    }, [getInitialToken])

    useEffect((): void => {
        getInitialToken()
    }, [getInitialToken])

    return { accessToken, renewToken }
}
